import {
    InputAdornment,
    InputBaseComponentProps,
    SxProps,
    TextField,
    Theme,
    styled,
} from '@mui/material';
import { isValid, parseISO } from 'date-fns';
import React, { CSSProperties, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

type Props = {
    value: string | undefined;
    label?: string;
    onChange: (value: string) => void;
    required?: boolean;
    disabled?: boolean;
    allowEmpty?: boolean;
    isDateAllowed?: (value?: string) => boolean;
    helperText?: string;
    labelOutside?: boolean;
    sx?: SxProps<Theme>;
    style?: CSSProperties;
    className?: string;
    inputProps?: InputBaseComponentProps;
};

const StyAdornment = styled(InputAdornment)(() => ({
    marginRight: '4px',
}));

const DateInput = ({
    value,
    label,
    onChange,
    sx,
    style,
    className,
    inputProps,
    isDateAllowed,
    helperText: helperTextProp,
    labelOutside = false,
    disabled = false,
    required = false,
    allowEmpty = false,
}: Props) => {
    const isDateValid = value === undefined ? true : isValid(parseISO(value));
    const { t } = useTranslation();

    // Adding an Adornement is a hacky way to force the outlined style
    // to never position the label inside the input, which cause a visual bug
    // when the value of the input is invalid.
    const labelOutsideProps = labelOutside
        ? {
            startAdornment: <StyAdornment position="start" />,
        }
        : {};

    const helperText = useMemo(() => {
        if (!allowEmpty && !isDateValid) {
            return t('date.invalid');
        }
        if (!isDateAllowed?.(value)) {
            return helperTextProp;
        }
        return null;
    }, [isDateValid, value, helperTextProp]);

    return (
        <TextField
            variant="outlined"
            value={value}
            type="date"
            required={required}
            disabled={disabled}
            label={label}
            className={className}
            onFocus={(e) => {
                e.target.select();
            }}
            onChange={(e) => {
                if (onChange) {
                    onChange(e.target.value);
                }
            }}
            sx={sx}
            InputProps={labelOutsideProps}
            error={!allowEmpty && (!isDateValid || (isDateAllowed !== undefined && !isDateAllowed(value)))}
            helperText={helperText}
            inputProps={inputProps}
            style={style}
        />
    );
};

export default DateInput;
